/**
 * @module SpendingsScreen
 */

import React, { PureComponent } from 'react';
import moment from 'moment';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { BDataGrid } from '../common/dataGrid/BDataGrid';
import SpendingsActionCreator from '../../actions/SpendingsActionCreator';
import SpendingsStore from '../../stores/SpendingsStore';
import config from '../../config';
import AnalyticsActionCreator from '../../actions/AnalyticsActionCreator';
import SpendingsHeaderProgress from './SpendingsHeaderProgress';
import SpendTableRowTemplate from './SpendTableRowTemplate';
import { SpendTableRowTemplateMobile } from './SpendTableRowTemplateMobile';
import UntaggedPage from './UntaggedPage';
import BrowserUtils from '../../utils/BrowserUtils';

class SpendingsScreen extends PureComponent {
  static propTypes = {
    content: PropTypes.object,
    spendListData: PropTypes.array,
    totalLastMonthValue: PropTypes.string,
    totalCurrentMonthValue: PropTypes.string,
    isNoBudget: PropTypes.bool,
    _detailedScreen: PropTypes.func,
  };
  state = {
    getAllAccountIds: SpendingsStore.updatedAccountIds(),
  };
  componentDidMount() {
    // Record an analytics user event.
    AnalyticsActionCreator.track(
      {
        path: '/user/experience/view',
        action: 'Appeared',
      },
      {
        description: config.analytics.analytics_name_bos_month,
      }
    );
  }
  onRowClick = (e, item) => {
    AnalyticsActionCreator.track(
      {
        path: '/user/experience/activity',
        action: 'Interacted',
      },
      {
        description: config.analytics.analytics_name_spend_view_transaction_tag,
        event: 'click',
      }
    );
    const date = new Date();
    const y = date.getFullYear();
    const m = date.getMonth();
    const firstDay = new Date(y, m, 1);
    const tagId = item.key.toString();
    const tagName = item.name;
    let filter_field;
    if (item.tag_budget) {
      filter_field = 'metadata.tags.id';
    } else {
      filter_field = 'metadata.categories.id';
    }
    const requestDataTemplate = {
      name: tagName,
      filter_value: tagId,
      filter_field,
      order: 'desc',
      size: 25,
      sort_field: 'details.when',
      start_date: moment(firstDay)
        .subtract(1, 'months')
        .format('YYYY-MM-DDTHH:mm:ss.[000+0100]'),
    };
    SpendingsActionCreator.getTransactionDetails(requestDataTemplate);
  };
  totalDaysLeft = () => {
    const currentDate = moment(moment().format('DD/MM/YYYY'), 'DD/MM/YYYY');
    const lastDateOfTheMonth = moment(
      moment().endOf('month').format('DD/MM/YYYY'),
      'DD/MM/YYYY'
    );
    return lastDateOfTheMonth.diff(currentDate, 'days');
  };
  renderScreenSwitch = () => {
    const {
      content,
      spendListData,
      totalLastMonthValue,
      totalCurrentMonthValue,
      isNoBudget,
      _detailedScreen,
    } = this.props;
    const monthNameCalculation = Number(moment().format('MM') - 2);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const labelLeft = content.spendingHeaderThisMonthText;
    const labelRight = moment().subtract(1, 'months').format('MMMM');
    const leftValue = Number(totalCurrentMonthValue);
    const rightValue = Number(totalLastMonthValue);

    if (!BrowserUtils.isMobileView()) {
      return (
        <div className="scroll-wrapper">
          <div className="spendings-month">
            <SpendingsHeaderProgress
              content={content}
              isNoBudget={isNoBudget}
              leftValue={leftValue}
              rightValue={rightValue}
              labelLeft={labelLeft}
              labelRight={labelRight}
              spendingProgressText={moment()
                .subtract(1, 'months')
                .format('MMMM')}
            />
            <div className="oldTable spendings-grid spendings content-wrapper">
              <BDataGrid
                results={spendListData}
                columns={[
                  'name',
                  'current_month',
                  'progress',
                  'last_month',
                  'message',
                ]}
                columnMetadata={columnMeta}
                initialSortAscending={false}
                customClick={this.onRowClick}
                initialSort="current_month"
                enableInfiniteScroll
                useFixedHeader
                showPager={false}
                resultsPerPage={spendListData.length}
                bodyHeight={400}
                content={content}
                showNoData
                noDataMessage={content.spendingTableLoading}
                tableClassName="table"
                useCustomPagerComponent="false"
              />
            </div>
          </div>
        </div>
      );
    } else {
      const matchedUntaggedData = _.find(spendListData, { name: 'Untagged' });
      const tableDataSorted = _.sortBy(spendListData, ['name']);

      const indexOfUntaggedData = tableDataSorted.indexOf(matchedUntaggedData);
      const untaggedDataPart = tableDataSorted.splice(indexOfUntaggedData, 1);

      const untaggedData = untaggedDataPart.map((item, index) => (
        <UntaggedPage
          rowData={item}
          customClick={this.onRowClick}
          content={content}
          key={`untaggedData_untaggedPage_${index}`}
        />
      ));

      const tableDataSortedFinal = _.sortedUniq(tableDataSorted);
      const monthScreenMobileRows = tableDataSortedFinal.map((item, index) => (
        <SpendTableRowTemplateMobile
          rowData={item}
          customClick={this.onRowClick}
          content={content}
          key={`monthScreenMobileRows_spendTableRowTemplateMobile_${index}`}
        />
      ));
      return (
        <div className="full-height spendings-budget">
          <div className="row no-gutters header-row">
            <div className="col-xs-6">
              <button
                className="page-back"
                id="0"
                type="button"
                onClick={_detailedScreen}
              >
                <span className="icon icon-page-back" />
                {content.back}
              </button>
            </div>
            <div className="col-xs-12 text-center">
              <h5>This month vs {_.nth(monthNames, monthNameCalculation)}</h5>
            </div>
          </div>
          <div className="calendar-row budget-calendar">
            <section className="text-center full-row">
              <h5>
                <span className="icon icon-calendar" />
                <b>{this.totalDaysLeft()}</b> {content.spendingDaysLeftText}
              </h5>
            </section>
          </div>
          {untaggedData}
          <div className="scroll-wrapper">{monthScreenMobileRows}</div>
        </div>
      );
    }
  };
  render() {
    return <div className="full-height">{this.renderScreenSwitch()}</div>;
  }
}

const columnMeta = [
  {
    columnName: 'name',
    order: 1,
    locked: false,
    visible: true,
    displayName: 'Tag',
    sortable: true,
    sortDirectionCycle: ['desc', 'asc'],
    customComponent: SpendTableRowTemplate,
    freeze: 'Untagged',
    cssClassName: 'column-tag',
  },
  {
    columnName: 'current_month',
    order: 2,
    locked: false,
    visible: true,
    displayName: 'Spent this month',
    sortable: true,
    customComponent: SpendTableRowTemplate,
    cssClassName: 'column-spent',
  },
  {
    columnName: 'progress',
    order: 3,
    locked: false,
    visible: true,
    displayName: ' ',
    sortable: false,
    customComponent: SpendTableRowTemplate,
    cssClassName: 'column-progress',
  },
  {
    columnName: 'last_month',
    order: 4,
    locked: false,
    visible: true,
    displayName: `Spent in ${moment().subtract(1, 'months').format('MMMM')}`,
    sortable: true,
    customComponent: SpendTableRowTemplate,
    cssClassName: 'column-amount',
  },
  {
    columnName: 'message',
    order: 5,
    locked: false,
    visible: true,
    displayName: 'Difference',
    sortable: false,
    customComponent: SpendTableRowTemplate,
    cssClassName: 'column-status',
  },
];

export default SpendingsScreen;
